import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Generic modal portal primitive used by every per-tab modal in later
 * phases. It renders into `document.body` so backdrop blur and z-index
 * stacking are not affected by ancestor `overflow:hidden` containers.
 *
 * Visual treatment (`.modal`, `.modal-content`, `.modal-header`,
 * `.modal-footer`, `.modal-close`) is provided by `styles/modals.css`,
 * which was extracted verbatim from the monolith.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   title?: React.ReactNode,
 *   titleIcon?: string,
 *   headerStyle?: React.CSSProperties, // background applied to the icon tile only, defaults to the app's indigo/purple gradient
 *   children: React.ReactNode,
 *   footer?: React.ReactNode,
 *   width?: string,
 *   ariaLabel?: string,
 *   dismissOnBackdrop?: boolean,
 *   dismissOnEscape?: boolean,
 *   id?: string,
 *   className?: string
 * }} props
 */
export default function Modal({
  open,
  onClose,
  title,
  titleIcon,
  headerStyle,
  children,
  footer,
  width,
  ariaLabel,
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  id,
  className = ''
}) {
  const backdropMouseDownRef = useRef(false);

  useEffect(() => {
    if (!open || !dismissOnEscape) return undefined;
    /** @param {KeyboardEvent} e */
    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, dismissOnEscape, onClose]);

  // Lock body scroll while a modal is open (matches monolith behaviour
  // — open modals freeze the underlying page).
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  /* Backdrop dismissal must check where the interaction STARTED, not just
     where the click event lands: selecting text or dragging inside the modal
     and releasing over the backdrop fires a click on the backdrop (the
     common ancestor), which used to close the modal mid-edit. */
  /** @param {React.MouseEvent<HTMLDivElement>} e */
  function onBackdropMouseDown(e) {
    backdropMouseDownRef.current = e.target === e.currentTarget;
  }

  /** @param {React.MouseEvent<HTMLDivElement>} e */
  function onBackdropClick(e) {
    const startedOnBackdrop = backdropMouseDownRef.current;
    backdropMouseDownRef.current = false;
    if (dismissOnBackdrop && startedOnBackdrop && e.target === e.currentTarget) onClose();
  }

  return createPortal(
    <div
      id={id}
      className={`modal modal--open modal--liquid ${className}`.trim()}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
      onMouseDown={onBackdropMouseDown}
      onClick={onBackdropClick}
    >
      <div className="modal-content" style={width ? { maxWidth: width } : undefined}>
        {(title || titleIcon) && (
          /* Quiet frosted header, dark text — matches the DetailSection
             card language. `headerStyle.background` (if a caller passes
             one, e.g. ConfirmModal's per-severity tint) colours only the
             small icon tile, not the whole bar, so no modal shouts. */
          <div className="modal-header">
            {titleIcon && (
              <div className="modal-header-icon" style={headerStyle}>
                <i className={`fas ${titleIcon}`} aria-hidden="true" />
              </div>
            )}
            <h3>{title}</h3>
            <button
              type="button"
              className="modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
